import { useCallback } from "react";
import { useSelector } from "react-redux";

import { useMedplum } from "@medplum/react";

import { RootState } from "@/store";
import { selectClientFlagsRevision } from "@/store/slices/client-flags";
import { getActiveOrganizationId } from "@/utils/medplum";

import { GET_CLIENT_FLAGS_FAILURE_MESSAGES } from "../constants/failure-messages";
import { FlagFailure } from "../constants/flag-failures";
import { ClientFlag } from "../interfaces/client-flags";
import { getClientFlags } from "../services/client-flags";
import { buildFlagReadScope } from "../utils/flag-read-scope";

import useFlagRead from "./useFlagRead";

interface ClientFlagsResult {
  flags: ClientFlag[];
  /** The first read for this client and practice is still in flight, so there is nothing to show. */
  isLoading: boolean;
  /**
   * The message to show in place of the rows, or `null` when there is nothing to say. A refused
   * read resolves to `null` too — see `GET_CLIENT_FLAGS_FAILURE_MESSAGES`.
   */
  errorMessage: string | null;
  /**
   * The read came back `Forbidden`: the user's role is not allowed to read flags. Tells a refused
   * read apart from a chart with no flags, which both come back with no rows and no message.
   */
  isRefused: boolean;
}

/** Shared so a chart with no flags hands its consumers the same array on every render. */
const NO_CLIENT_FLAGS: ClientFlag[] = [];

/**
 * Reads the library flags applied to one client's chart.
 *
 * The practice comes from the active organization rather than the route: the client profile is
 * mounted at `/clients/:clientId`, which carries no practice of its own.
 *
 * Reads again whenever the client's flags change from the drawer, wherever it was opened.
 *
 * @param clientId - Patient logical id whose chart to read
 * @param enabled - Whether the surface asking is showing; a hidden one reads nothing
 */
export default function useClientFlags(clientId: string, enabled = true): ClientFlagsResult {
  const medplum = useMedplum();
  // Resolved in render rather than inside the read: the practice is half of the query, so it has to
  // take part in the scope as the client does.
  const practiceId = getActiveOrganizationId();

  const read = useCallback(
    () => getClientFlags({ medplum, clientId, practiceId }),
    [medplum, clientId, practiceId],
  );

  const revision = useSelector((state: RootState) => selectClientFlagsRevision(state, clientId));

  const { data, isLoading, errorMessage, failureReason } = useFlagRead({
    read,
    enabled,
    revision,
    scope: buildFlagReadScope(practiceId, clientId),
    failureMessages: GET_CLIENT_FLAGS_FAILURE_MESSAGES,
  });

  return {
    flags: data ?? NO_CLIENT_FLAGS,
    isLoading,
    errorMessage,
    isRefused: failureReason === FlagFailure.Forbidden,
  };
}
